import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    RightOutlined,
    CarOutlined,
    UserOutlined,
    CalendarOutlined,
    TagOutlined,
    IdcardOutlined,
    EditOutlined,
    ArrowLeftOutlined,
    SettingOutlined,
    TeamOutlined,
    DashboardOutlined,
    EnvironmentOutlined
} from '@ant-design/icons';
import { vehiculesServices } from '../../common/services';
import {
    Spin, Descriptions, Tag, Typography, Card, Breadcrumb, Tabs,
    Row, Col, Grid, Avatar, Statistic, Button, message
} from 'antd';
import dayjs from 'dayjs';

const { Title, Text } = Typography;

// Couleurs pour les catégories
const CATEGORY_COLORS = {
    'econom': '#52c41a',
    'comfort': '#1890ff',
    'comfort_plus': '#722ed1',
    'business': '#fa8c16',
    'start': '#13c2c2',
    'standart': '#2f54eb',
    'moto': '#eb2f96',
    'intercity': '#faad14',
    'courier': '#f5222d',
    'express': '#fa541c',
    'pool': '#a0d911',
    'minivan': '#597ef7',
};

const getCategoryColor = (category) => {
    const cat = category?.toLowerCase();
    return CATEGORY_COLORS[cat] || '#8c8c8c';
};

// Status labels et couleurs
const STATUS_CONFIG = {
    'working': { label: 'En service', color: 'green' },
    'not_working': { label: 'Hors service', color: 'red' },
    'repairing': { label: 'En réparation', color: 'orange' },
    'no_driver': { label: 'Sans chauffeur', color: 'volcano' },
    'pending': { label: 'En attente', color: 'gold' },
    'unknown': { label: 'Inconnu', color: 'default' },
};

const DetailVehicule = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [vehicule, setVehicule] = useState(null);
    const [loading, setLoading] = useState(true);

    // Breakpoints & tailles adaptatives
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;
    const controlSize = isMobile ? 'small' : 'middle';

    const formatDate = (date) => {
        if (!date) return '-';
        const parsed = dayjs(date);
        return parsed.isValid() ? parsed.format('DD/MM/YYYY HH:mm') : '-';
    };

    // Chargement initial
    useEffect(() => {
        if (!id) return;
        setLoading(true);
        vehiculesServices.getById(id)
            .then((data) => {
                setVehicule(data);
            })
            .catch((err) => {
                console.error('Erreur chargement véhicule :', err);
                message.error('Erreur lors du chargement du véhicule');
            })
            .finally(() => setLoading(false));
    }, [id]);

    if (loading || !vehicule) {
        return (
            <div style={{ padding: 40, textAlign: 'center', minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Spin tip="Chargement des détails..." size="large" />
            </div>
        );
    }

    const vehicleLabel = `${vehicule.marque} ${vehicule.modele}`.trim();
    const statusConfig = STATUS_CONFIG[vehicule.status] || STATUS_CONFIG['unknown'];

    // Onglets config
    const tabItems = [
        {
            key: '1',
            label: (
                <span><CarOutlined style={{ marginRight: 6 }} />Informations</span>
            ),
            children: (
                <Card
                    style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    bodyStyle={{ padding: isMobile ? 12 : 24 }}
                >
                    <Descriptions
                        bordered
                        column={{ xs: 1, sm: 1, md: 2 }}
                        size={controlSize}
                        labelStyle={{
                            fontWeight: 500,
                            backgroundColor: '#fafafa',
                            width: isMobile ? 140 : 180
                        }}
                        contentStyle={{ backgroundColor: 'white' }}
                    >
                        <Descriptions.Item label={<><CarOutlined style={{ marginRight: 8, color: '#1890ff' }} />Marque</>}>
                            <Text strong>{vehicule.marque}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={<><CarOutlined style={{ marginRight: 8, color: '#52c41a' }} />Modèle</>}>
                            <Text strong>{vehicule.modele || '-'}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={<><TagOutlined style={{ marginRight: 8, color: '#722ed1' }} />Immatriculation</>}>
                            <Tag color="blue" style={{ fontSize: 14, fontWeight: 500 }}>{vehicule.immatriculation}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 8, color: '#fa8c16' }} />Année</>}>
                            <Text>{vehicule.annee}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={<><span style={{ marginRight: 8, color: '#eb2f96' }}>🎨</span>Couleur</>}>
                            <Text>{vehicule.couleur}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={<><SettingOutlined style={{ marginRight: 8, color: '#13c2c2' }} />Status</>}>
                            <Tag color={statusConfig.color}>{statusConfig.label}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label={<><DashboardOutlined style={{ marginRight: 8, color: '#2f54eb' }} />Kilométrage</>}>
                            <Text>{vehicule.kilometrage?.toLocaleString('fr-FR') || 0} km</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={<><EnvironmentOutlined style={{ marginRight: 8, color: '#1890ff' }} />Ville</>}>
                            <Text>{vehicule.city?.name || '-'}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label={<><IdcardOutlined style={{ marginRight: 8, color: '#1890ff' }} />ID Yango</>}>
                            <Text code style={{ fontSize: 11 }}>{vehicule.carYangoId || '-'}</Text>
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
            )
        },
        {
            key: '2',
            label: (
                <span><TagOutlined style={{ marginRight: 6 }} />Classes de service</span>
            ),
            children: (
                <Card
                    style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    bodyStyle={{ padding: isMobile ? 12 : 24 }}
                >
                    <div style={{ marginBottom: 16 }}>
                        <Text type="secondary">Catégorie principale :</Text>
                        <div style={{ marginTop: 8 }}>
                            {vehicule.categorieServiceId?.name ? (
                                <Tag color="purple" style={{ fontSize: 14, padding: '4px 12px' }}>
                                    {vehicule.categorieServiceId.name}
                                </Tag>
                            ) : '-'}
                        </div>
                    </div>
                    <div>
                        <Text type="secondary">Classes de service actives :</Text>
                        <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {(vehicule.categories || []).length > 0 ? (
                                vehicule.categories.map((cat, idx) => (
                                    <Tag
                                        key={idx}
                                        style={{
                                            backgroundColor: getCategoryColor(cat),
                                            color: 'white',
                                            border: 'none',
                                            fontWeight: 500,
                                            fontSize: 13,
                                            padding: '4px 12px'
                                        }}
                                    >
                                        {cat.toUpperCase()}
                                    </Tag>
                                ))
                            ) : (
                                <Text type="secondary">Aucune classe de service</Text>
                            )}
                        </div>
                    </div>
                </Card>
            )
        },
        {
            key: '3',
            label: (
                <span><TeamOutlined style={{ marginRight: 6 }} />Chauffeurs</span>
            ),
            children: (
                <Card
                    style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    bodyStyle={{ padding: isMobile ? 12 : 24 }}
                >
                    <Descriptions
                        bordered
                        column={1}
                        size={controlSize}
                        labelStyle={{
                            fontWeight: 500,
                            backgroundColor: '#fafafa',
                            width: isMobile ? 140 : 200
                        }}
                    >
                        <Descriptions.Item label="Chauffeur actif">
                            {vehicule.chauffeurActif ? (
                                <Tag color="green">{vehicule.chauffeurActif}</Tag>
                            ) : (
                                <Text type="secondary">Aucun</Text>
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Chauffeurs liés">
                            {(vehicule.chauffeurs || []).length > 0 ? (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                    {vehicule.chauffeurs.map((ch, idx) => (
                                        <Tag key={idx} color="blue">{ch}</Tag>
                                    ))}
                                </div>
                            ) : (
                                <Text type="secondary">Aucun chauffeur lié</Text>
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Propriétaire">
                            {vehicule.userId ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
                                    <span>
                                        {vehicule.userId.firstName} {vehicule.userId.lastName}
                                        {vehicule.userId.phone && (
                                            <Text type="secondary" style={{ marginLeft: 8 }}>({vehicule.userId.phone})</Text>
                                        )}
                                    </span>
                                </div>
                            ) : (
                                <Text type="secondary">Non assigné</Text>
                            )}
                        </Descriptions.Item>
                        <Descriptions.Item label="Ambassadeur">
                            {vehicule.ambassadorId ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <Avatar size="small" icon={<UserOutlined />} style={{ backgroundColor: '#722ed1' }} />
                                    <span>
                                        {vehicule.ambassadorId.firstName} {vehicule.ambassadorId.lastName}
                                        {vehicule.ambassadorId.phone && (
                                            <Text type="secondary" style={{ marginLeft: 8 }}>({vehicule.ambassadorId.phone})</Text>
                                        )}
                                    </span>
                                    {vehicule.linkedToAmbassadorAt && (
                                        <Text type="secondary" style={{ marginLeft: 8 }}>
                                            (lié le {formatDate(vehicule.linkedToAmbassadorAt)})
                                        </Text>
                                    )}
                                </div>
                            ) : (
                                <Text type="secondary">Aucun ambassadeur</Text>
                            )}
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
            )
        },
        {
            key: '4',
            label: (
                <span><CalendarOutlined style={{ marginRight: 6 }} />Historique</span>
            ),
            children: (
                <Card
                    style={{ borderRadius: 12, border: 'none', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}
                    bodyStyle={{ padding: isMobile ? 12 : 24 }}
                >
                    <Descriptions
                        bordered
                        column={1}
                        size={controlSize}
                        labelStyle={{
                            fontWeight: 500,
                            backgroundColor: '#fafafa',
                            width: isMobile ? 140 : 200
                        }}
                    >
                        <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 8, color: '#52c41a' }} />Date de création</>}>
                            {formatDate(vehicule.createdAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label={<><CalendarOutlined style={{ marginRight: 8, color: '#1890ff' }} />Dernière modification</>}>
                            {formatDate(vehicule.updatedAt)}
                        </Descriptions.Item>
                        <Descriptions.Item label={<><IdcardOutlined style={{ marginRight: 8, color: '#722ed1' }} />ID MongoDB</>}>
                            <Text code style={{ fontSize: 11 }}>{vehicule._id}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="Marqué pour suppression">
                            {vehicule.markedForDeletion ? (
                                <Tag color="red">Oui - {formatDate(vehicule.deletedAt)}</Tag>
                            ) : (
                                <Tag color="green">Non</Tag>
                            )}
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
            )
        }
    ];

    return (
        <div className="content-wrapper">
            <style>
                {`
                    .ant-tabs-tab { font-weight: 500; }
                    .ant-tabs-tab-active { color: #1890ff !important; }
                    .ant-tabs-ink-bar { background: #1890ff !important; }
                `}
            </style>

            <div className="container-xxl flex-grow-1" style={{ paddingTop: 8, paddingLeft: 24, paddingRight: 24 }}>
                {/* Breadcrumb */}
                <Breadcrumb
                    separator={<RightOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />}
                    style={{ marginBottom: 12, marginTop: 0, paddingTop: 0 }}
                >
                    <Breadcrumb.Item>
                        <Link to="/dashboard" style={{ color: '#1890ff' }}>Accueil</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <Link to="/dashboard/vehicules" style={{ color: '#1890ff' }}>Véhicules</Link>
                    </Breadcrumb.Item>
                    <Breadcrumb.Item>
                        <span style={{ color: '#595959' }}>Détails</span>
                    </Breadcrumb.Item>
                </Breadcrumb>

                {/* Header avec actions */}
                <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                    <Col>
                        <Button
                            icon={<ArrowLeftOutlined />}
                            onClick={() => navigate('/dashboard/vehicules')}
                            style={{ marginRight: 16 }}
                        >
                            Retour
                        </Button>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            icon={<EditOutlined />}
                            onClick={() => navigate(`/dashboard/vehicule/${id}/modifier`)}
                            style={{
                                borderRadius: 8,
                                background: 'linear-gradient(135deg, #52c41a 0%, #95de64 100%)',
                                border: 'none'
                            }}
                        >
                            Modifier
                        </Button>
                    </Col>
                </Row>

                {/* Statistiques rapides */}
                <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
                    <Col xs={12} sm={6}>
                        <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c' }}><CarOutlined /> Véhicule</span>}
                                value={vehicleLabel}
                                valueStyle={{ color: '#1890ff', fontSize: 16 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c' }}><TagOutlined /> Immatriculation</span>}
                                value={vehicule.immatriculation}
                                valueStyle={{ color: '#722ed1', fontSize: 16 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c' }}><SettingOutlined /> Status</span>}
                                value={statusConfig.label}
                                valueStyle={{ color: statusConfig.color === 'green' ? '#52c41a' : (statusConfig.color === 'red' ? '#ff4d4f' : '#fa8c16'), fontSize: 16 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={6}>
                        <Card style={{ borderRadius: 12, textAlign: 'center' }} bodyStyle={{ padding: 16 }}>
                            <Statistic
                                title={<span style={{ color: '#8c8c8c' }}><TeamOutlined /> Chauffeurs</span>}
                                value={(vehicule.chauffeurs || []).length}
                                valueStyle={{ color: '#52c41a', fontSize: 20 }}
                            />
                        </Card>
                    </Col>
                </Row>

                {/* Onglets */}
                <Card
                    style={{ borderRadius: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                    bodyStyle={{ padding: isMobile ? 12 : 24 }}
                >
                    <Tabs
                        defaultActiveKey="1"
                        size={controlSize}
                        items={tabItems}
                        tabBarGutter={isMobile ? 8 : 24}
                    />
                </Card>
            </div>
        </div>
    );
};

export default DetailVehicule;
